/** ⌘K (design 11 §1.5): go to any section, or run a command. Searching resources comes with their pages. */
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { LogOut, Moon, Sun } from "lucide-react";
import { useT } from "../i18n";
import { SECTIONS } from "./sections";
import type { Theme } from "./theme";

const ITEM = "flex cursor-default items-center gap-2 rounded px-3 py-2 text-sm data-[selected=true]:bg-[var(--color-surface)]";
const GROUP =
  "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-[var(--color-muted)]";

export function CommandPalette({ open, onOpenChange, onTheme, onSignOut }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTheme: (theme: Theme) => void;
  onSignOut: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-24 w-full max-w-lg -translate-x-1/2 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] shadow-xl"
        >
          <Dialog.Title className="sr-only">{t("palette.label")}</Dialog.Title>
          <Command label={t("palette.label")}>
            <Command.Input
              autoFocus
              placeholder={t("palette.placeholder")}
              className="w-full border-b border-[var(--color-border)] bg-transparent px-4 py-3 outline-none"
            />
            <Command.List className="max-h-80 overflow-y-auto p-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-[var(--color-muted)]">
                {t("palette.empty")}
              </Command.Empty>
              <Command.Group heading={t("palette.sections")} className={GROUP}>
                {SECTIONS.map((s) => (
                  <Command.Item key={s.path} value={t(s.label)} className={ITEM} onSelect={() => run(() => void navigate({ to: s.path }))}>
                    <s.icon size={16} aria-hidden="true" />
                    {t(s.label)}
                  </Command.Item>
                ))}
              </Command.Group>
              <Command.Group heading={t("palette.commands")} className={GROUP}>
                <Command.Item className={ITEM} onSelect={() => run(() => onTheme("light"))}>
                  <Sun size={16} aria-hidden="true" />
                  {t("palette.themeLight")}
                </Command.Item>
                <Command.Item className={ITEM} onSelect={() => run(() => onTheme("dark"))}>
                  <Moon size={16} aria-hidden="true" />
                  {t("palette.themeDark")}
                </Command.Item>
                <Command.Item className={ITEM} onSelect={() => run(onSignOut)}>
                  <LogOut size={16} aria-hidden="true" />
                  {t("user.signOut")}
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
